/**
 * @coeldery/audio-player — 歌曲播放器（**兩個密度**：`mini` 常駐底條／`full` 播放器頁）
 *
 * 為何要模組：任何「生成／收藏音頻」嘅 app 都要同一個播放器（第二個 app 都會用）→ R3 做模組。
 *
 * 規格：
 *   · `mini`：一粒 play/pause ＋ 歌名 ＋ 幼進度條 ＋ 時間；**整條可以撳**（`onOpen` → 去播放器頁）
 *   · `full`：歌名 ＋ 可拖 seek（`input[type=range]`）＋ prev/play/next ＋ **高音質切換**（冇 flac 就停用）
 *             ＋ 錯誤原樣顯示（`rules.md` B2）＋ 可選歌詞
 *   · ⚠️ 模組**唔碰音頻**（唔建立 `Audio`）—— 播放狀態由父層持有（呢個 app 係全 app 單一 player）
 *   · 文字一律 i18n key；顏色只用 CSS var
 */
import { useTranslation } from 'react-i18next';

export interface AudioPlayerProps {
  variant?: 'mini' | 'full';
  title: string;
  playing: boolean;
  positionMs: number;
  durationMs: number | null;
  /** 而家係唔係高音質 */
  hifi?: boolean;
  /** 有冇 flac 檔案（**冇就唔准顯示為可用**） */
  hifiAvailable?: boolean;
  error?: string | null;
  lyrics?: string | null;
  onToggle: () => void;
  onSeek?: (ms: number) => void;
  onNext?: () => void;
  onPrev?: () => void;
  onToggleHifi?: (on: boolean) => void;
  /** mini：撳條身 → 開播放器頁 */
  onOpen?: () => void;
}

/** 毫秒 → `m:ss`（未知回 `-`） */
function fmt(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '-';
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const circleBtn = (primary = false): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: primary ? 'none' : '1.5px solid var(--color-divider)',
  borderRadius: '999px',
  background: primary ? 'var(--color-primary)' : 'var(--color-card)',
  color: primary ? 'var(--color-on-primary)' : 'var(--color-text)',
  fontFamily: 'inherit',
  fontSize: '18px',
  cursor: 'pointer',
});

export default function AudioPlayer({
  variant = 'mini',
  title,
  playing,
  positionMs,
  durationMs,
  hifi = false,
  hifiAvailable = false,
  error = null,
  lyrics = null,
  onToggle,
  onSeek,
  onNext,
  onPrev,
  onToggleHifi,
  onOpen,
}: AudioPlayerProps) {
  const { t } = useTranslation();
  const ratio = durationMs && durationMs > 0 ? Math.min(1, Math.max(0, positionMs / durationMs)) : 0;

  if (variant === 'mini') {
    return (
      <div
        role="group"
        aria-label={t('player_now_playing')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 12px',
          background: 'var(--color-card)',
          borderTop: '1px solid var(--color-divider)',
          boxShadow: 'var(--shadow-subtle, none)',
        }}
      >
        <button
          type="button"
          aria-label={playing ? t('player_pause') : t('player_play')}
          onClick={onToggle}
          style={{ ...circleBtn(true), width: '40px', height: '40px' }}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <button
          type="button"
          onClick={onOpen}
          disabled={!onOpen}
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            padding: 0,
            fontFamily: 'inherit',
            color: 'var(--color-text)',
            cursor: onOpen ? 'pointer' : 'default',
          }}
        >
          <span
            style={{
              display: 'block',
              fontSize: '14.5px',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </span>
          {/* 幼進度條：mini 唔需要拖（要拖去 full 頁） */}
          <span style={{ display: 'block', height: '3px', marginTop: '6px', borderRadius: '999px', background: 'var(--color-divider)' }}>
            <span
              style={{ display: 'block', width: `${ratio * 100}%`, height: '100%', borderRadius: '999px', background: 'var(--color-primary)' }}
            />
          </span>
          <span style={{ display: 'block', marginTop: '4px', fontSize: 'var(--font-size-tiny, 13.5px)', color: 'var(--color-text-secondary)' }}>
            {fmt(positionMs)} / {fmt(durationMs)}
          </span>
        </button>
        {error ? (
          <span role="alert" style={{ fontSize: 'var(--font-size-tiny, 13.5px)', color: 'var(--color-danger)' }}>
            {t('player_error')}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <section
      aria-label={t('player_now_playing')}
      style={{
        margin: '12px',
        padding: '18px 16px',
        borderRadius: '20px',
        background: 'var(--color-card)',
        border: '1px solid var(--color-divider)',
        boxShadow: 'var(--shadow-soft, none)',
      }}
    >
      <h2 style={{ margin: '0 0 4px', fontSize: '20px', color: 'var(--color-text)', textAlign: 'center' }}>{title}</h2>
      <p style={{ margin: '0 0 14px', fontSize: 'var(--font-size-tiny, 13.5px)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
        {hifi ? t('player_hifi_on') : t('player_hifi_off')}
      </p>

      <input
        type="range"
        min={0}
        max={durationMs ?? 0}
        value={Math.round(positionMs)}
        onChange={(e) => onSeek?.(Number(e.target.value))}
        disabled={!durationMs || !onSeek}
        aria-label={t('player_seek')}
        style={{ width: '100%', accentColor: 'var(--color-primary)' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-tiny, 13.5px)', color: 'var(--color-text-secondary)' }}>
        <span>{fmt(positionMs)}</span>
        <span>{fmt(durationMs)}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', marginTop: '14px' }}>
        <button type="button" aria-label={t('player_prev')} onClick={onPrev} disabled={!onPrev} style={{ ...circleBtn(), width: '48px', height: '48px' }}>
          ⏮
        </button>
        <button
          type="button"
          aria-label={playing ? t('player_pause') : t('player_play')}
          onClick={onToggle}
          style={{ ...circleBtn(true), width: '64px', height: '64px', fontSize: '24px' }}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <button type="button" aria-label={t('player_next')} onClick={onNext} disabled={!onNext} style={{ ...circleBtn(), width: '48px', height: '48px' }}>
          ⏭
        </button>
      </div>

      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <button
          type="button"
          onClick={() => onToggleHifi?.(!hifi)}
          disabled={!hifiAvailable}
          style={{
            minHeight: '40px',
            padding: '0 16px',
            borderRadius: '20px',
            border: '1.5px solid var(--color-primary)',
            background: hifi ? 'var(--color-primary)' : 'var(--color-card)',
            color: hifi ? 'var(--color-on-primary)' : 'var(--color-primary-strong, var(--color-primary))',
            fontFamily: 'inherit',
            fontSize: '14.5px',
            fontWeight: 700,
            cursor: hifiAvailable ? 'pointer' : 'not-allowed',
            opacity: hifiAvailable ? 1 : 0.5,
          }}
        >
          {hifi ? t('player_hifi_on') : t('player_hifi_off')}
        </button>
      </div>

      {error ? (
        <p role="alert" style={{ margin: '12px 0 0', color: 'var(--color-danger)', fontSize: 'var(--font-size-small, 14px)', textAlign: 'center' }}>
          {t('player_error')}（{error}）
        </p>
      ) : null}

      {lyrics ? (
        <p
          style={{
            margin: '16px 0 0',
            whiteSpace: 'pre-wrap',
            fontSize: '14.5px',
            lineHeight: 1.8,
            color: 'var(--color-text)',
            maxHeight: '38vh',
            overflowY: 'auto',
          }}
        >
          {lyrics}
        </p>
      ) : null}
    </section>
  );
}

